"use client";

import { useCallback } from "react";

import type { AnalyticsEventMap } from "./events";
import { capture } from "./posthog";

type Required = AnalyticsEventMap["lock viewed"]["required"];

/**
 * One impression per surface per page view. Re-armed on every client-side
 * navigation by `rearmLockImpressions()` (AnalyticsProvider calls it when the
 * pathname changes), so a Back to a page already seen counts again — as its
 * "paywall hit" clicks do, which this event is the denominator for.
 */
const seen = new Set<string>();

/** A new page view started (pathname changed): every surface may fire again. */
export function rearmLockImpressions(): void {
  seen.clear();
}

/**
 * Ref callback that reports a "lock viewed" event the first time half of the
 * element is in the viewport. Pass `enabled: false` while the viewer's plan is
 * still resolving — a signed-in user reads as "anonymous" for a moment, and an
 * impression then would count them behind a sign-in wall they never saw.
 *
 * Kept in its own module (not posthog.ts) so components that mock the
 * analytics module with `{ capture }` keep working.
 */
export function useLockImpression(
  surface: string | undefined,
  { required, plan, enabled = true }: { required: Required; plan: string; enabled?: boolean },
): (node: Element | null) => void | (() => void) {
  return useCallback(
    (node: Element | null) => {
      if (!node || !surface || !enabled) return;
      if (typeof IntersectionObserver === "undefined") return;
      const key = `${location.pathname}|${surface}`;
      if (seen.has(key)) return;
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          if (seen.has(key)) return;
          seen.add(key);
          capture("lock viewed", { surface, required, plan });
        },
        { threshold: 0.5 },
      );
      io.observe(node);
      return () => io.disconnect();
    },
    [surface, required, plan, enabled],
  );
}

/** Test hook: forget which surfaces were reported. */
export function resetLockImpressionsForTests(): void {
  seen.clear();
}
